import { Direction } from '@angular/cdk/bidi';
import { Overlay } from '@angular/cdk/overlay';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CardDesignComponent } from 'src/app/@shared/components/design/slide-up-card/card-design.component';
import { SelectOption } from 'src/app/@shared/interfaces/select-option.interface';
import { ServiceResponse } from 'src/app/@shared/interfaces/service-response.interface';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { LocalStorageService } from 'src/app/@shared/services/local-storage/local-storage.service';
import { ModalService } from 'src/app/@shared/services/modal/modal.service';
import { QueryParamsService } from 'src/app/@shared/services/query-params/query-params.service';
import { ResizeService } from 'src/app/@shared/services/resize/resize.service';
import { RouteFiltersComponent } from 'src/app/modules/route-planner/components/route-planner-form/forms/source-destination-form/route-filters/route-filters.component';
import { SourceDestinationDetailedResultComponent } from 'src/app/modules/route-planner/components/route-planner-results/results/source-destination/source-destination-detailed-result/source-destination-detailed-result.component';
import {
  MORE_ACTIONS_MENU,
  PRINT,
  PRINT_ARIA_LABEL,
  ROUTE_PLANNING,
  SHARE,
  THE_REQUESTED_PATH,
  ROUTE_DETAILS,
  ORIGIN,
  DESTINATION,
  INTERCITY_LINES,
} from 'src/app/modules/route-planner/constants/multi-language.constants';
import { eTab } from 'src/app/modules/route-planner/enums/route-planning/tabs/tabs.enums';
import { RoutePlanning } from 'src/app/modules/route-planner/interfaces/route-planning/@route-planning.interface';
import { LinesBetweenCitiesForm } from 'src/app/modules/route-planner/interfaces/route-planning/forms/lines-between-cities-form';
import { IntercityLines } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/intercity-lines.interface';
import { PrintService } from 'src/app/modules/route-planner/services/print/print.service';
import { RoutePlanningService } from 'src/app/modules/route-planner/services/route-planning/route-planning.service';
import { ScheduleService } from 'src/app/modules/route-planner/services/schedule/schedule.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-lines-between-cities-results-container',
    templateUrl: './lines-between-cities-results-container.component.html',
    styleUrls: ['./lines-between-cities-results-container.component.scss'],
    standalone: false
})
export class LinesBetweenCitiesResultsContainerComponent
  extends CardDesignComponent
  implements OnInit {
  @ViewChild('card') override card;

  formValues: LinesBetweenCitiesForm;
  results: IntercityLines[];
  appliedFilters: boolean;
  moreActionsMenuItems: SelectOption<string>[] = [
    {
      label: 'הדפס',
      value: 'print',
      icon: 'print',
      onClick: () => this.handlePrint(),
    },
  ];

  isDataOver: boolean = false;

  // Add a flag to track the current results key
  private currentResultsKey: string = '';

  noResultsForText: string;
  shareText: string;
  routePlanningText: string;
  moreActionsMenuText: string;

  direction: Direction;

  isLoadingMoreData: boolean = false

  constructor(
    routePlanningService: RoutePlanningService,
    resizeService: ResizeService,
    overlay: Overlay,

    private readonly modalService: ModalService,
    private readonly queryParamsService: QueryParamsService,
    private readonly localStorageService: LocalStorageService,
    private readonly scheduleService: ScheduleService,
    private readonly activeRoute: ActivatedRoute,
    private readonly languageService: LanguageService,
    private readonly printService: PrintService
  ) {
    super(routePlanningService, resizeService, overlay);
  }

  get isPrinting(): boolean {
    return this.printService.isPrinting;
  }

  override ngOnInit(): void {
    super.ngOnInit();

    console.debug('LinesBetweenCitiesResultsContainer initializing');

    // Subscribe to form data and results
    this.routePlanningService._formSubject.subscribe((routePlanning) => {
      if (this.queryParamsService._currentParams['tab'] !== eTab.INTER_CITY_LINE) {
        return;
      }
      if (!routePlanning?.formValues || !routePlanning?.results) {
        console.debug('Received empty form data or results');
        return;
      }

      const formValues = routePlanning.formValues as LinesBetweenCitiesForm;

      // Create a unique key for these results
      const originCity = formValues.origin?.cityName || '';
      const destCity = formValues.destination?.cityName || '';
      const resultsKey = `${originCity}-${destCity}`;

      console.debug('Received form data and results:', {
        resultsKey,
        currentResultsKey: this.currentResultsKey,
        resultsCount: routePlanning.results?.length || 0,
      });

      // If we already have results for this key, don't update
      if (
        this.currentResultsKey === resultsKey &&
        this.results &&
        this.results.length > 0
      ) {
        console.debug('Skipping duplicate results for the same cities');
        return;
      }

      // Update the results and form values
      console.debug('Setting results and form values for new cities');
      this.results = routePlanning.results;
      this.formValues = formValues;
      this.currentResultsKey = resultsKey;

      // Update URL parameters for sharing
      if (
        this.formValues &&
        this.formValues.origin &&
        this.formValues.destination
      ) {
        console.debug('Updating URL parameters for sharing');
        this.queryParamsService.setQueryParams({
          tab: eTab.INTER_CITY_LINE,
          originCity: this.formValues.origin.cityName,
          destinationCity: this.formValues.destination.cityName,
          saveParams: true,
        });
      }
    });

    // Subscribe to loading state
    this.routePlanningService.getLoadingState().subscribe((isLoading) => {
      console.debug('Loading state changed:', isLoading);
      this.isLoadingData = isLoading;
    });

    this.appliedFilters = !!this.localStorageService.getItem('filters');
    this.initializeMultiLanguageTexts();
  }

  initializeMultiLanguageTexts() {
    this.languageService.selectedLanguage$.subscribe((language) => {
      if(this.direction) {
        this.closeCard(true);
        return;
      }
      this.direction = language.direction;
      this.noResultsForText = THE_REQUESTED_PATH[language.language];
      this.shareText = SHARE[language.language];
      this.routePlanningText = INTERCITY_LINES[language.language];
      this.moreActionsMenuItems[0].label = PRINT[language.language];
      this.moreActionsMenuItems[0].ariaLabel = PRINT_ARIA_LABEL[language.language];
      this.moreActionsMenuText = MORE_ACTIONS_MENU[language.language];
    });
  }

  override handleOverlayEscape(): void {
    this.closeCard();
  }

  handleResultClick(interCityLine: IntercityLines) {

    const trustedBase = environment.maslulan_url.replace(/\/+$/, '');
    const url = `${trustedBase}/?tab=lines&saveParams=true&routeId=${interCityLine.routeId}&line=${interCityLine.routeName}&oppositeRouteId=${interCityLine.oppositeRouteId}&boardingStation=${interCityLine.originStopCode}&dropingStation=${interCityLine.destinationStopCode}`;

    window.open(url, '_blank', 'noopener,noreferrer');
  }

  shareResults() {
    if (
      this.formValues &&
      this.formValues.origin &&
      this.formValues.destination
    ) {
      this.queryParamsService.setQueryParams({
        tab: eTab.INTER_CITY_LINE,
        originCity: this.formValues.origin.cityName,
        destinationCity: this.formValues.destination.cityName,
        saveParams: true,
      });

      //this.queryParamsService.preserveShareableParams();
    }

    this.modalService.openShareLinkModal();
  }

  onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const atBottom =
      element.scrollHeight - element.scrollTop === element.clientHeight;

    const timeType =
      this.activeRoute.snapshot.queryParamMap.get('timeType') || 'DEPART';
    if (atBottom && !this.isDataOver && timeType === 'DEPART') {
      const lastData = this.results[this.results.length - 1];

      const timeStamp =
        this.activeRoute.snapshot.queryParamMap.get('time') || Date.now();
      const date = new Date(parseInt(timeStamp.toString()));
      const previousTime = date.getTime();
      const [hours, minutes, seconds] =
        timeType === 'DEPART'
          ? lastData.originDepartureTime.split(':').map(Number)
          : lastData.destinationArrivalTime.split(':').map(Number);
      date.setHours(hours, minutes, seconds + 1, 0);

      if (date.getTime() <= previousTime) {
        date.setDate(date.getDate() + 1);
      }

      this.isLoadingMoreData = true
      const additionalLinesObs$ = this.scheduleService.getIntercityLines({
        origin: lastData.originCity.he,
        destination: lastData.destinationCity.he,
        departureTime: date.toISOString(),
        isNewSearch: false
      });
      additionalLinesObs$.subscribe({
        next: (additonalLines: ServiceResponse<IntercityLines[]>) => {
          if (additonalLines.data.length) {
            const currentResultsIds = this.results.map(l => l.id)
            const newLines = additonalLines.data.filter(l => !currentResultsIds.includes(l.id))
            this.results = [...this.results, ...newLines];

          } else {
            this.isDataOver = true;
          }
          this.isLoadingMoreData = false

        },
        error: () => {
          this.isLoadingMoreData = false
        }
      }
      );
    }
  }

  handlePrint() {
    // Show loading indicator immediately - use setTimeout to allow browser to render
    this.printService.showLoadingIndicator();
    
    // Use setTimeout(0) to yield to browser rendering before continuing
    setTimeout(() => {
      const printableElement = document.getElementById('printableArea');
      if (!printableElement) {
        console.warn('No printable content found!');
        return;
      }

      // Don't try to find or click a map tab since there is no map
      this.printContent(printableElement);
    }, 0);
  }

  private printContent(printableElement: HTMLElement) {
    const currentLanguage = this.languageService.getSelectedLanguage().language;

    const headerInfo = {
      title: ROUTE_DETAILS[currentLanguage],
      details: [
        {
          label: ORIGIN[currentLanguage],
          value: this.formValues.origin.cityName || 'N/A',
        },
        {
          label: DESTINATION[currentLanguage],
          value: this.formValues.destination.cityName || 'N/A',
        },
      ],
    };

    // Pass null instead of mapService since there's no map to capture
    this.printService.captureAndPrint(
      printableElement,
      headerInfo as any,
      null, // Don't pass the map service
      2,
      null, // No print selector
      4 // 4 results per page
    );
  }
}
